import './Tag.module.css'

import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { MainLayout } from '@/components/layout/MainLayout'
import { Spinner } from '@/components/spinner'
import { useAppContext } from '@/context/app'
import { Roles } from '@/services/repository/users'

type ResponseLastSession = {
  userId: string
  startAt: string
}

type LastChecklist = {
  userId: string
  status: string
  createdAt: string
}

type Response = {
  inited: boolean
  privileged: boolean
  emachReady: boolean
  targetType?: string | null
  targetId?: string | null
  machineId: string
  lastSession: ResponseLastSession | null
  lastUserSession: ResponseLastSession | null
  lastChecklist: LastChecklist | null
  lastUserChecklist: LastChecklist | null
}

export const QRTag = () => {
  const navigate = useNavigate()
  const { services, session, translates } = useAppContext()
  const [permissionsError, setPermissionsError] = useState<boolean>(false)
  const [generalError, setGeneralError] = useState<boolean>(false)

  const qrid = useParams().id

  if (session === undefined) {
    return <Spinner />
  }

  function hasRole(roles: Roles[]): boolean {
    const role = session?.user.role as Roles
    return role && roles.includes(role)
  }

  function routing(resp: Response) {
    setPermissionsError(false)
    if (resp.targetType === 'inventory_item') {
      const search = new URLSearchParams()
      if (resp.targetId) {
        search.set('search', resp.targetId)
      }
      navigate({
        pathname: '/communal/remanent/inventory-items/manager',
        search: search.toString(),
      })
      return
    }
    if (hasRole([Roles.SuperAdmin, Roles.HyperAdmin])) {
      if (!resp.inited) {
        navigate(`/qr/activate/${qrid}`)
        return
      }
      navigate(`/communal/machine/${resp.machineId}`)
      return
    }

    if (!resp.inited) {
      setPermissionsError(true)
      return
    }

    if (hasRole([Roles.Operator])) {
      if (!resp.privileged) {
        setPermissionsError(true)
        return
      }
      if (!resp.emachReady) {
        setGeneralError(true)
        return
      }

      const isOlderThan8Hours = (startAt: string): boolean => {
        const EIGHT_HOURS = 8 * 60 * 60 * 1000
        const lastStarted = startAt
          ? new Date(startAt).getTime()
          : null
        return !!(lastStarted && (Date.now() - lastStarted >= EIGHT_HOURS))
      }
      if (!resp.lastUserChecklist || resp.lastUserChecklist.userId != session?.user.id
        || isOlderThan8Hours(resp.lastUserChecklist.createdAt)) {
        navigate(`/communal/machine/${resp.machineId}/checklist/wizard`)
        return
      }

      navigate(`/communal/machine/${resp.machineId}/activate`)
      return
    }

    navigate(`/communal/machine/${resp.machineId}`)
  }

  useEffect(() => {
    setGeneralError(false)
    const fetchURL = `/api/communal/qr/byid/${qrid}/codeinfo`
    services.session.fetchWithToken(fetchURL).then((resp) => {
      if (!resp.ok) {
        setGeneralError(true)
        console.error(`${fetchURL} fetch data error`)
        return
      }
      resp.json().then((data) => {
        routing(data)
      })
    })
  }, [qrid])

  if (!permissionsError && !generalError) {
    return <Spinner />
  }

  return <MainLayout services={services} translates={translates} pageName='qrtag'>
    {permissionsError ? <div className='critic-error-box'>{translates?.for('errors.no_permissions')}</div> : null}
    {generalError ? <div className='critic-error-box'>{translates?.for('errors.general_error')}</div> : null}
  </MainLayout>
}
